(() => {
  if (customElements.get('ggx-portal-scene')) return;

  class GeekGrindPortalScene extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      if (this.dataset.portalMotion !== 'true') return;
      const stage = this.querySelector('.ggx-threshold');
      const picture = stage?.querySelector('.ggx-picture');
      const image = picture?.querySelector('img');
      const art = stage?.querySelector('[data-portal-art]');
      const planes = Array.from(art?.querySelectorAll('[data-portal-plane]') || []);
      const artImages = Array.from(art?.querySelectorAll('[data-portal-image]') || []);
      const controls = this.querySelector('[data-portal-controls]');
      const motion = controls?.querySelector('[data-portal-choice="motion"]');
      const still = controls?.querySelector('[data-portal-choice="still"]');
      const status = controls?.querySelector('[data-portal-status]');
      if (!image || !art || planes.length !== 4 || !motion || !still || !status) return;
      const controller = new AbortController();
      const signal = controller.signal;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      const desktop = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
      const supported = typeof window.SVGClipPathElement !== 'undefined' &&
        typeof window.IntersectionObserver === 'function' &&
        typeof window.ResizeObserver === 'function';
      const ownerDocument = this.ownerDocument;
      let requested = !reduce.matches && desktop.matches;
      let sourceMatches = false;
      let inView = false;
      let alive = true;
      let pageActive = true;
      let frame = 0;
      let previousTime = 0;
      let x = 0;
      let y = 0;
      let targetX = 0;
      let targetY = 0;
      let scale = 1;
      let bounds = null;
      const capable = () => supported && sourceMatches && desktop.matches && !reduce.matches;
      const active = () => alive && requested && capable() && inView && pageActive && !ownerDocument.hidden;
      const draw = () => {
        for (const plane of planes) {
          const amplitude = Math.min(Number(plane.dataset.amplitude) / scale, 5.5);
          plane.setAttribute('transform', `translate(${(-x * amplitude).toFixed(4)} ${(-y * amplitude * 0.55).toFixed(4)})`);
        }
      };
      const reset = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        previousTime = 0;
        x = y = targetX = targetY = 0;
        draw();
      };
      const render = () => {
        const available = capable();
        const selected = available && requested;
        const running = active();
        if (running) {
          for (const layerImage of artImages) {
            if (!layerImage.hasAttribute('href')) layerImage.setAttribute('href', layerImage.dataset.source);
          }
        }
        this.dataset.portalActive = String(running);
        motion.disabled = !available;
        motion.setAttribute('aria-pressed', String(selected));
        still.setAttribute('aria-pressed', String(!selected));
        const label = reduce.matches ? status.dataset.reducedLabel :
          !desktop.matches ? status.dataset.touchLabel :
          !available ? status.dataset.unavailableLabel :
          selected ? status.dataset.readyLabel : status.dataset.stillLabel;
        if (status.textContent !== label) status.textContent = label;
        if (!active()) reset();
      };
      const tick = (time) => {
        frame = 0;
        if (!active()) { reset(); return; }
        const delta = previousTime ? Math.min(time - previousTime, 50) : 16.67;
        previousTime = time;
        const easing = 1 - Math.pow(0.84, delta / 16.67);
        x += (targetX - x) * easing;
        y += (targetY - y) * easing;
        if (Math.abs(targetX - x) + Math.abs(targetY - y) < 0.0005) {
          x = targetX;
          y = targetY;
          previousTime = 0;
        } else {
          frame = requestAnimationFrame(tick);
        }
        draw();
      };
      const schedule = () => {
        if (!frame && active() && (Math.abs(targetX - x) + Math.abs(targetY - y) > 0.0001)) {
          frame = requestAnimationFrame(tick);
        }
      };
      const measure = () => {
        bounds = stage.getBoundingClientRect();
        scale = Math.max(bounds.width / 1672, bounds.height / 941, 0.001);
        reset();
      };
      const verifySource = () => {
        let sameSource = false;
        try {
          sameSource = new URL(image.currentSrc || image.src, ownerDocument.baseURI).href ===
            new URL(this.dataset.portalSource, ownerDocument.baseURI).href;
        } catch (_) { sameSource = false; }
        const style = window.getComputedStyle(image);
        sourceMatches = sameSource && image.complete && image.naturalWidth === 1672 &&
          image.naturalHeight === 941 && style.objectFit === 'cover' &&
          (style.objectPosition === '50% 50%' || style.objectPosition === 'center center');
        measure();
        render();
      };
      const onPointer = (event) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        if (!active()) return;
        bounds = stage.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        targetX = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1));
        targetY = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1));
        schedule();
      };
      const onLeave = () => { targetX = targetY = 0; schedule(); };
      const onMedia = () => {
        if (reduce.matches || !desktop.matches) requested = false;
        verifySource();
      };
      motion.addEventListener('click', () => { if (capable()) requested = true; render(); }, { signal });
      still.addEventListener('click', () => { requested = false; render(); }, { signal });
      stage.addEventListener('pointerenter', onPointer, { signal, passive: true });
      stage.addEventListener('pointermove', onPointer, { signal, passive: true });
      stage.addEventListener('pointerleave', onLeave, { signal, passive: true });
      stage.addEventListener('pointercancel', onLeave, { signal, passive: true });
      image.addEventListener('load', verifySource, { signal });
      image.addEventListener('error', () => { sourceMatches = false; render(); }, { signal });
      reduce.addEventListener('change', onMedia, { signal });
      desktop.addEventListener('change', onMedia, { signal });
      ownerDocument.addEventListener('visibilitychange', render, { signal });
      window.addEventListener('pagehide', () => { pageActive = false; render(); }, { signal });
      window.addEventListener('pageshow', () => { pageActive = true; verifySource(); }, { signal });
      ownerDocument.addEventListener('shopify:section:load', (event) => {
        if (event.target === this || event.target.contains(this)) this.connectedCallback();
      }, { signal });
      const visibility = supported ? new IntersectionObserver((entries) => {
        inView = entries.some(entry => entry.target === stage && entry.isIntersecting);
        render();
      }, { threshold: 0 }) : null;
      const size = supported ? new ResizeObserver(measure) : null;
      const source = new MutationObserver(verifySource);
      visibility?.observe(stage);
      size?.observe(stage);
      source.observe(picture, { subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'media'] });
      this.cleanup = () => {
        alive = false;
        controller.abort();
        visibility?.disconnect();
        size?.disconnect();
        source.disconnect();
        reset();
        this.dataset.portalActive = 'false';
        controls.hidden = true;
      };
      controls.hidden = false;
      verifySource();
    }

    disconnectedCallback() { this.cleanup?.(); }
  }

  customElements.define('ggx-portal-scene', GeekGrindPortalScene);
})();
